import type { ReactNode } from 'react';
import { BookCard } from './BookCard';
import { cn } from '@/lib/cn';
import type { BookView } from '@/types/api';

/**
 * BookGrid — responsive flex-wrap grid used by `/catalog` and `/list`.
 *
 * The repeating unit is the same `BookCard` the carousels render, so a
 * user can click a result on `/library`, see it in `/catalog`, and open
 * the same modal with the same hover affordance. The grid takes over at
 * ≥sm where the viewport can fit at least two 240px cards; below that
 * cards wrap one per row.
 *
 * Loading, empty, and "no books match your filters" are first-class
 * branches so callers don't each invent their own copy. The empty state
 * is overridable via `emptyState` for pages that want to encourage a
 * specific next action (e.g. "browse the Library" for /list).
 */
export interface BookGridProps {
  books: BookView[];
  onSelectBook: (book: BookView) => void;
  loading?: boolean;
  /** Custom empty-state slot. When omitted, a neutral default is rendered. */
  emptyState?: ReactNode;
  /** Number of skeleton cards to render while `loading` is true. */
  skeletonCount?: number;
  /**
   * Optional per-card action overlay. Rendered absolute-positioned in the
   * top-right of the card — used by the Reading List to expose "Remove".
   */
  renderBadge?: (book: BookView) => ReactNode;
  className?: string;
}

const DEFAULT_SKELETON_COUNT = 12;

export function BookGrid({
  books,
  onSelectBook,
  loading = false,
  emptyState,
  skeletonCount = DEFAULT_SKELETON_COUNT,
  renderBadge,
  className,
}: BookGridProps) {
  if (loading) {
    return (
      <div
        className={cn('flex flex-wrap gap-4', className)}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label="Loading books"
      >
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <BookGridSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (books.length === 0) {
    return (
      <div className={className}>
        {emptyState ?? (
          <div className="rounded-2xl border border-dashed border-or-slate-200 bg-white/60 px-6 py-12 text-center text-[13px] text-or-slate-500">
            No books to show.
          </div>
        )}
      </div>
    );
  }

  return (
    <ul
      className={cn('flex flex-wrap gap-4', className)}
      // Using role=list keeps VoiceOver announcing the grouping since the
      // individual cards don't carry list semantics of their own.
      role="list"
    >
      {books.map((book) => (
        <li key={book.id} className="relative">
          <BookCard book={book} onSelect={onSelectBook} />
          {renderBadge ? (
            <div className="pointer-events-auto absolute right-1.5 top-1.5">
              {renderBadge(book)}
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function BookGridSkeleton() {
  return (
    <div className="flex w-[140px] flex-shrink-0 flex-col gap-2 sm:w-[240px]" aria-hidden>
      <div className="aspect-[240/264] w-full animate-pulse rounded-xl bg-or-slate-200" />
      <div className="h-3 w-4/5 animate-pulse rounded bg-or-slate-200" />
      <div className="h-3 w-1/2 animate-pulse rounded bg-or-slate-100" />
    </div>
  );
}
